import type {
  RepositoryIntelligenceAnalysis,
  RepositoryIntelligenceAnalysisList,
  RepositoryIntelligenceAnalysisReceipt,
  RepositoryIntelligenceAnalysisRequest,
  RepositoryIntelligenceProposalResult,
} from '@ashniva/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '../../shared/lib/api-client';
import { workPlanKeys } from '../projects/work-plan-api';
import { intelligencePath, riKeys, riMutationRetry, riRetry, type Submission } from './api';
import { usePollWindow } from './use-poll-window';

/** Change analyses of a repository, followed until done, and the proposals made from their suggested tasks. */

const analysesPath = (linkId: string) => `${intelligencePath(linkId)}/change-analyses`;
const analysesKey = (linkId: string) => [...riKeys.link(linkId), 'analyses'] as const;

export type AnalysisSource =
  | { source: 'commit_range'; base: string; head: string }
  | { source: 'task'; taskId: string }
  | { source: 'pull_request'; prNumber: number };

export function useRequestAnalysis(linkId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AnalysisSource & Submission) =>
      apiRequest<RepositoryIntelligenceAnalysisReceipt>(analysesPath(linkId), {
        method: 'POST',
        body: input satisfies RepositoryIntelligenceAnalysisRequest,
      }),
    retry: riMutationRetry,
    onSuccess: () => client.invalidateQueries({ queryKey: analysesKey(linkId) }),
  });
}

const pollAfterOfAnalysis = (data: RepositoryIntelligenceAnalysis | undefined) =>
  data ? data.analysis.pollAfterMs : undefined;

/** One analysis, polled while it runs (`pollAfterMs`, with back-off, for two minutes). */
export function useAnalysisQuery(linkId: string, analysisId: string | null) {
  const poll = usePollWindow(analysisId, Boolean(analysisId), pollAfterOfAnalysis);
  const query = useQuery({
    queryKey: [...analysesKey(linkId), analysisId ?? ''],
    queryFn: () =>
      apiRequest<RepositoryIntelligenceAnalysis>(
        `${analysesPath(linkId)}/${encodeURIComponent(analysisId ?? '')}`,
      ),
    enabled: Boolean(analysisId),
    retry: riRetry,
    refetchInterval: poll.refetchInterval,
    refetchIntervalInBackground: true,
  });
  const final = query.data ? query.data.analysis.pollAfterMs === null : false;
  return {
    query,
    stillWorking: poll.stillWorking && !final,
    checkAgain: () => {
      poll.restart();
      void query.refetch();
    },
  };
}

export function useAnalysesQuery(linkId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...analysesKey(linkId), 'list'],
    queryFn: () =>
      apiRequest<RepositoryIntelligenceAnalysisList>(analysesPath(linkId), {
        query: { limit: 20 },
      }),
    enabled,
    retry: riRetry,
  });
}

/** Only the ordinals go up: titles and descriptions come from Repository Intelligence, on the server. */
export function useProposeTasks(linkId: string, analysisId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ordinals: number[]) =>
      apiRequest<RepositoryIntelligenceProposalResult>(
        `${analysesPath(linkId)}/${encodeURIComponent(analysisId)}/proposals`,
        { method: 'POST', body: { ordinals } },
      ),
    // The project's Summary lists proposals; it reads them fresh.
    onSuccess: (result) =>
      client.invalidateQueries({ queryKey: workPlanKeys.proposals(result.projectId) }),
  });
}
